// `useSubscribed<T>(getValue, subscribe)` — Preact hook that mirrors a
// `Subscribable<T>` (or any object with `getValue` + `subscribe`) into
// component-local state. Hoisted at the 5th consumer per OC §3.3 +
// the D-082 / D-083 forward notes (Store + persistence-error +
// lastSavedAt + pendingWrites + toast queue).
//
// **Why hand-rolled (not `useSyncExternalStore`).** Preact's
// `useSyncExternalStore` lives in `preact/compat`, which the project
// hasn't introduced anywhere else. Matching the existing `useStore`
// shape (D-082) keeps the hook surface uniform; if the project later
// standardises on `useSyncExternalStore`, swap inside this one file.
//
// **Effect ordering.** `useState(() => getValue())` seeds the render
// with the current value (avoids a flash of stale data). The mount
// effect re-syncs once (Preact's setState bails on referential
// identity, so a no-op call is cheap) to close the theoretical race
// where the source mutates between the lazy initializer's read and
// the effect commit. Then it subscribes and returns the unsubscribe
// callback as the cleanup so the listener detaches on unmount.

import { useEffect, useState } from 'preact/hooks'

export function useSubscribed<T>(
  getValue: () => T,
  subscribe: (listener: (value: T) => void) => () => void,
): T {
  const [value, setValue] = useState<T>(() => getValue())
  useEffect(() => {
    setValue(getValue())
    return subscribe(setValue)
  }, [])
  return value
}
